<template>
  <div class="dashboard-page">
    <page-loading
      v-if="loading && !hasLoadedOnce"
      message="Carregando dashboard"
      subtitle="Calculando indicadores do período"
      :preview-count="4"
    />
    <template v-else>
    <v-row class="dash-banners-row" dense align="stretch">
      <v-col
        v-if="showOnboardingAlert"
        cols="12"
        :md="bannerColMd"
        class="dash-banners-row__col"
      >
        <v-alert
          dense
          type="warning"
          outlined
          border="left"
          :icon="false"
          class="dash-banner dash-banner--onboarding fill-height mb-2 mb-md-0"
        >
          <div class="dash-banner__inner">
            <v-icon class="dash-banner__lead-icon" color="amber darken-2" size="20">
              mdi-clipboard-check-outline
            </v-icon>
            <div class="dash-banner__copy">
              <span class="dash-banner__title">Finalize seu cadastro</span>
              <span class="dash-banner__hint">
                Libere pagamentos e ganhe visibilidade no app.
              </span>
              <v-tooltip bottom max-width="280">
                <template #activator="{ on, attrs }">
                  <v-icon x-small class="dash-banner__info" v-bind="attrs" v-on="on">mdi-information-outline</v-icon>
                </template>
                Falta pouco para liberar pagamentos instantâneos e mais visibilidade. Conclua o checklist para colher os benefícios.
              </v-tooltip>
            </div>
            <v-btn
              small
              depressed
              color="primary"
              class="dash-banner__cta"
              @click="goToOnboardingChecklist"
            >
              Concluir
            </v-btn>
          </div>
        </v-alert>
      </v-col>

      <v-col
        v-if="showPremiumAlert"
        cols="12"
        :md="bannerColMd"
        class="dash-banners-row__col"
      >
        <partner-premium-banner fill-height />
      </v-col>
    </v-row>

    <p class="dash-subtitle">{{ welcomeSubtitle }}</p>

    <page-toolbar>
      <template #filters>
        <div class="dash-period-field">
          <v-dialog ref="dialog" v-model="dialog" persistent width="290px">
            <template v-slot:activator="{ on, attrs }">
              <v-text-field
                :value="formattedPeriod"
                label="Período"
                prepend-inner-icon="mdi-calendar-range"
                readonly
                dense
                outlined
                hide-details
                class="br-form-field"
                v-bind="attrs"
                v-on="on"
              />
            </template>
            <v-date-picker v-model="selectedRange" range locale="pt-br">
              <v-btn @click="closeDialog" color="primary" text>Cancelar</v-btn>
              <v-btn @click="handleLoadDashboard" color="primary">Aplicar</v-btn>
            </v-date-picker>
          </v-dialog>
        </div>
      </template>

      <template #actions>
        <v-tooltip bottom>
          <template v-slot:activator="{ on, attrs }">
            <v-btn
              icon
              small
              :loading="inlineRefreshing"
              :disabled="loading && !hasLoadedOnce"
              aria-label="Atualizar"
              v-bind="attrs"
              v-on="on"
              @click="refreshDashboard"
            >
              <v-icon>mdi-refresh</v-icon>
            </v-btn>
          </template>
          Atualizar indicadores
        </v-tooltip>
        <v-chip class="br-chip-count update-chip">
          <v-icon left small>mdi-calendar-clock</v-icon>
          Atualizado: {{ lastUpdatedLabel }}
        </v-chip>
        <v-card
          v-if="showWithdrawFeature"
          flat
          class="balance-pill"
          @click="openWithdrawModal"
        >
          <v-card-text class="balance-pill__inner">
            <v-icon color="primary" size="20">mdi-wallet-outline</v-icon>
            <div class="balance-pill__text">
              <span class="balance-pill__value">
                {{ showBalances ? formatBalance($store.state.sessionInfo.balance) : "R$ ••••" }}
              </span>
              <span class="balance-pill__label">Bônus de indicação</span>
            </div>
          </v-card-text>
        </v-card>
        <v-tooltip bottom>
          <template v-slot:activator="{ on, attrs }">
            <v-btn
              v-bind="attrs"
              v-on="on"
              outlined
              small
              color="primary"
              class="toggle-balances-btn"
              @click="toggleBalances"
            >
              <v-icon left small>{{ showBalances ? "mdi-eye-off" : "mdi-eye" }}</v-icon>
              {{ showBalances ? "Ocultar saldos" : "Mostrar saldos" }}
            </v-btn>
          </template>
          {{ showBalances ? "Ocultar valores nos cartões" : "Mostrar valores nos cartões" }}
        </v-tooltip>
      </template>

      <span class="quick-actions-inline__label">Acesso rápido</span>
      <v-btn
        v-for="item in quickActionItems"
        :key="item.title"
        :to="item.to"
        small
        text
        color="primary"
        class="quick-actions-inline__btn"
      >
        <v-icon left small :color="item.iconColor">{{ item.icon }}</v-icon>
        {{ item.title }}
      </v-btn>
    </page-toolbar>

    <div v-if="loading" class="mb-2">
      <v-row dense>
        <v-col v-for="i in 4" :key="`sk-${i}`" cols="12" sm="6" lg="3">
          <v-skeleton-loader type="card" boilerplate class="kpi-skeleton" />
        </v-col>
      </v-row>
    </div>

    <v-row v-else dense class="metrics-row">
      <v-col cols="12" sm="6" lg="3">
        <partner-summary-metric-card
          title="Faturamento"
          :value="showBalances ? formatPrice(dashboard.invoicing) : getMaskedValue()"
          icon="mdi-cash-multiple"
          variant="finance"
          tooltip="Valor total faturado no período selecionado."
        />
      </v-col>
      <v-col cols="12" sm="6" lg="3">
        <partner-summary-metric-card
          title="Lucro"
          :value="showBalances ? formatPrice(dashboard.profit) : getMaskedValue()"
          icon="mdi-chart-line"
          variant="finance"
          tooltip="Resultado após custos no período."
        />
      </v-col>
      <v-col cols="12" sm="6" lg="3">
        <partner-summary-metric-card
          title="Lucro de indicação"
          :value="showBalances ? formatPrice(dashboard.indicationProfit) : getMaskedValue()"
          icon="mdi-account-star-outline"
          variant="brand"
          tooltip="Ganhos com indicações no período."
        />
      </v-col>
      <v-col cols="12" sm="6" lg="3">
        <partner-summary-metric-card
          title="Consumidores"
          :value="dashboard.totalCustomers"
          icon="mdi-account-group"
          variant="network"
          tooltip="Clientes cadastrados no período selecionado."
        />
      </v-col>
    </v-row>

    <app-modal
      v-if="showWithdrawFeature"
      v-model="withdrawDialog"
      title="Solicitar saque"
      icon="mdi-cash"
      :max-width="500"
    >
      <div class="mb-4">
        <p class="text-subtitle-2 mb-1 secondary--text">Saldo disponível (bônus de indicação)</p>
        <p class="text-h5 font-weight-bold primary--text">
          {{ formatBalance($store.state.sessionInfo.balance) }}
        </p>
      </div>

      <v-text-field
        v-model="withdrawAmount"
        label="Valor do saque"
        prefix="R$"
        :rules="withdrawRules"
        :error-messages="withdrawErrors"
        outlined
        dense
        class="mb-3 br-form-field"
        @input="formatCurrency"
      />

      <v-text-field
        v-model="withdrawPassword"
        label="Senha"
        type="password"
        :rules="passwordRules"
        outlined
        dense
        class="mb-3 br-form-field"
      />

      <v-alert
        v-if="withdrawAmount && parseFloat(withdrawAmount.replace(/[^\d]/g, '')) / 100 < 25"
        type="warning"
        dense
        outlined
        class="mb-3"
      >
        Valor mínimo para saque é R$ 25,00
      </v-alert>

      <v-alert
        v-if="
          withdrawAmount &&
            parseFloat(withdrawAmount.replace(/[^\d]/g, '')) / 100 >
              (typeof $store.state.sessionInfo.balance === 'string'
                ? parseFloat($store.state.sessionInfo.balance)
                : $store.state.sessionInfo.balance)
        "
        type="error"
        dense
        outlined
        class="mb-3"
      >
        Valor solicitado excede o saldo disponível
      </v-alert>

      <template #actions>
        <v-btn text @click="closeWithdrawModal">Cancelar</v-btn>
        <v-btn
          color="primary"
          :dark="isWithdrawValid"
          :disabled="!isWithdrawValid"
          :loading="withdrawLoading"
          depressed
          @click="processWithdraw"
        >
          Confirmar saque
        </v-btn>
      </template>
    </app-modal>
    </template>
  </div>
</template>

<script lang="ts">
import { defineComponent } from "vue";
import { GetDashboard } from "@/services/dashboard/GetDashboard";
import { DashboardType } from "@/types/dashboard/Dashboard";
import { WithdrawTransaction } from "@/services/transaction/WithdrawTransaction";
import { GetAllIndications } from "@/services/indications/GetAllIndications";
import { GetAllCustomers } from "@/services/customers/GetAllCustomers";
import PartnerPremiumBanner from "@/components/premium/PartnerPremiumBanner.vue";
import PartnerSummaryMetricCard from "@/components/summary/PartnerSummaryMetricCard.vue";
import PageLoading from "@/components/loading/PageLoading.vue";
import PageToolbar from "@/components/ui/PageToolbar.vue";
import AppModal from "@/components/ui/AppModal.vue";

type QuickActionItem = {
  title: string;
  icon: string;
  to: string;
  iconColor: string;
};

export default defineComponent({
  components: { AppModal, PageToolbar, PartnerPremiumBanner, PartnerSummaryMetricCard, PageLoading },

  data() {
    const today = new Date();
    const thirtyDaysAgo = new Date();
    thirtyDaysAgo.setDate(today.getDate() - 30);
    const formattedToday = today.toISOString().split("T")[0];
    const formattedThirtyDaysAgo = thirtyDaysAgo.toISOString().split("T")[0];
    return {
      dialog: false,
      showBalances: false,
      selectedRange: [formattedThirtyDaysAgo, formattedToday],
      startDate: formattedThirtyDaysAgo,
      endDate: formattedToday,
      mobileBreakpoint: 600,
      loading: true,
      inlineRefreshing: false,
      hasLoadedOnce: false,
      lastUpdatedAt: null as Date | null,
      dashboard: {
        indicationProfit: 0,
        invoicing: 0,
        totalCustomers: 0,
        profit: 0,
      } as DashboardType,
      withdrawDialog: false,
      withdrawAmount: "",
      withdrawPassword: "",
      withdrawLoading: false,
      withdrawErrors: [] as string[],
      showWithdrawFeature: true,
    };
  },
  computed: {
    formattedPeriod(): string {
      const parts = (this.selectedRange || [])
        .filter(Boolean)
        .map((date: string) => {
          const [year, month, day] = date.split("-");
          return `${day}/${month}/${year}`;
        });
      return parts.join(" — ") || "Selecione o período";
    },
    greetingFirstName(): string {
      const raw = this.$store.state.sessionInfo?.name?.trim();
      if (!raw) return "";
      return raw.split(/\s+/)[0] ?? "";
    },
    welcomeSubtitle(): string {
      if (this.greetingFirstName) {
        return `Olá, ${this.greetingFirstName} — visão geral do seu desempenho no período.`;
      }
      return "Visão geral do seu desempenho no período selecionado.";
    },
    lastUpdatedLabel(): string {
      if (!this.lastUpdatedAt) return "—";
      return new Intl.DateTimeFormat("pt-BR", {
        dateStyle: "short",
        timeStyle: "short",
      }).format(this.lastUpdatedAt);
    },
    quickActionItems(): QuickActionItem[] {
      return [
        { title: "Vendas", icon: "mdi-cart-outline", to: "/sales", iconColor: "#E72A4C" },
        { title: "Clientes", icon: "mdi-account-group-outline", to: "/clients", iconColor: "#242968" },
        { title: "Indicações", icon: "mdi-account-plus-outline", to: "/indications", iconColor: "#1a7f5a" },
        { title: "Meus dados", icon: "mdi-card-account-details-outline", to: "/my-data", iconColor: "#c25a00" },
      ];
    },
    withdrawRules(): any[] {
      return [
        (v: string) => !!v || "Valor é obrigatório",
        (v: string) => {
          const cleanAmount = v.replace(/[^\d]/g, "");
          const amount = parseFloat(cleanAmount) / 100;
          return (!Number.isNaN(amount) && amount > 0) || "Valor deve ser maior que zero";
        },
        (v: string) => {
          const cleanAmount = v.replace(/[^\d]/g, "");
          const amount = parseFloat(cleanAmount) / 100;
          return (!Number.isNaN(amount) && amount >= 25) || "Valor mínimo é R$ 25,00";
        },
        (v: string) => {
          const cleanAmount = v.replace(/[^\d]/g, "");
          const amount = parseFloat(cleanAmount) / 100;
          const availableBalance =
            typeof this.$store.state.sessionInfo.balance === "string"
              ? parseFloat(this.$store.state.sessionInfo.balance)
              : this.$store.state.sessionInfo.balance;
          return (!Number.isNaN(amount) && amount <= availableBalance) || "Valor excede o saldo disponível";
        },
      ];
    },
    passwordRules(): any[] {
      return [
        (v: string) => !!v || "Senha é obrigatória",
        (v: string) => (v && v.length >= 6) || "Senha deve ter pelo menos 6 caracteres",
      ];
    },
    isWithdrawValid(): boolean {
      const cleanAmount = this.withdrawAmount.replace(/[^\d]/g, "");
      const amount = parseFloat(cleanAmount) / 100;
      const availableBalance =
        typeof this.$store.state.sessionInfo.balance === "string"
          ? parseFloat(this.$store.state.sessionInfo.balance)
          : this.$store.state.sessionInfo.balance;

      return (
        !Number.isNaN(amount) &&
        amount > 0 &&
        amount >= 25 &&
        amount <= availableBalance &&
        Boolean(this.withdrawPassword) &&
        this.withdrawPassword.length >= 6
      );
    },
    showOnboardingAlert(): boolean {
      return Boolean(this.$store.state.onboardingPending);
    },
    showPremiumAlert(): boolean {
      return !this.$store.state.sessionInfo.isPremium;
    },
    bannerColMd(): number {
      return this.showOnboardingAlert && this.showPremiumAlert ? 6 : 12;
    },
  },
  mounted() {
    this.handleLoadDashboard();
  },
  methods: {
    closeDialog() {
      this.dialog = false;
    },
    refreshDashboard() {
      void this.handleLoadDashboard({ silent: true });
    },
    async handleLoadDashboard(options?: { silent?: boolean }) {
      const silent = options?.silent === true;
      if (silent) {
        this.inlineRefreshing = true;
      } else {
        this.loading = true;
      }
      const getDashboard = new GetDashboard();
      if (this.selectedRange.length > 0) {
        if (this.selectedRange[0] > this.selectedRange[1]) {
          this.startDate = this.selectedRange[1];
          this.endDate = this.selectedRange[0];
        } else {
          this.startDate = this.selectedRange[0];
          this.endDate = this.selectedRange[1];
        }
      }

      try {
        const dashboard = await getDashboard.execute(this.startDate, this.endDate);

        const getAllIndications = new GetAllIndications();
        const indications = await getAllIndications.execute(this.startDate, this.endDate);
        const indicationProfit = indications.reduce((total, item) => total + (item.value || 0), 0);

        const getAllCustomers = new GetAllCustomers();
        const allCustomers = await getAllCustomers.execute();

        const filteredCustomers = allCustomers.filter((customer) => {
          if (!customer.createdAt) return false;

          const customerDate = new Date(customer.createdAt);
          const startDateObj = new Date(this.startDate);
          const endDateObj = new Date(this.endDate);

          startDateObj.setHours(0, 0, 0, 0);
          endDateObj.setHours(23, 59, 59, 999);

          return customerDate >= startDateObj && customerDate <= endDateObj;
        });

        const totalCustomers = filteredCustomers.length;

        this.dashboard = {
          ...dashboard,
          indicationProfit,
          totalCustomers,
        };
        this.lastUpdatedAt = new Date();
        this.hasLoadedOnce = true;
      } catch (error) {
        console.error("Erro ao carregar dashboard:", error);
        this.$store.dispatch("showSnack", {
          message: "Erro ao carregar dados do dashboard",
          color: "error",
        });
      } finally {
        this.loading = false;
        this.inlineRefreshing = false;
        this.closeDialog();
      }
    },
    formatPrice(value: number): string {
      if (!value && value !== 0) return "";
      const formattedValue = value.toFixed(2).replace(".", ",");
      return `R$ ${formattedValue}`;
    },
    goToOnboardingChecklist() {
      this.$router.push({ name: "onboardingChecklist" });
    },
    toggleBalances() {
      this.showBalances = !this.showBalances;
    },
    getMaskedValue(): string {
      return "R$ ••••";
    },
    formatBalance(value: number | string): string {
      const numValue = typeof value === "string" ? parseFloat(value) : value;
      if (!numValue && numValue !== 0) return "R$ 0,00";
      const formattedValue = numValue.toFixed(2).replace(".", ",");
      return `R$ ${formattedValue}`;
    },
    openWithdrawModal() {
      this.withdrawDialog = true;
      this.withdrawAmount = "";
      this.withdrawPassword = "";
      this.withdrawErrors = [];
    },
    closeWithdrawModal() {
      this.withdrawDialog = false;
      this.withdrawAmount = "";
      this.withdrawPassword = "";
      this.withdrawErrors = [];
    },
    formatCurrency() {
      const cleanValue = this.withdrawAmount.replace(/[^\d]/g, "");

      if (cleanValue === "") {
        this.withdrawAmount = "";
        return;
      }

      const numericValue = parseInt(cleanValue, 10) / 100;

      const formattedValue = numericValue.toLocaleString("pt-BR", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });

      this.withdrawAmount = formattedValue;
      this.validateWithdrawAmount();
    },
    validateWithdrawAmount() {
      this.withdrawErrors = [];

      const cleanAmount = this.withdrawAmount.replace(/[^\d]/g, "");
      const amount = parseFloat(cleanAmount) / 100;

      if (Number.isNaN(amount) || amount <= 0) {
        this.withdrawErrors.push("Valor deve ser maior que zero");
        return;
      }

      if (amount < 25) {
        this.withdrawErrors.push("Valor mínimo para saque é R$ 25,00");
        return;
      }

      const availableBalance =
        typeof this.$store.state.sessionInfo.balance === "string"
          ? parseFloat(this.$store.state.sessionInfo.balance)
          : this.$store.state.sessionInfo.balance;

      if (amount > availableBalance) {
        this.withdrawErrors.push("Valor solicitado excede o saldo disponível");
      }
    },
    async processWithdraw() {
      if (!this.isWithdrawValid) return;

      this.withdrawLoading = true;
      try {
        const withdrawService = new WithdrawTransaction();
        const cleanAmount = this.withdrawAmount.replace(/[^\d]/g, "");
        const amount = parseFloat(cleanAmount) / 100;
        await withdrawService.execute(amount, this.withdrawPassword);

        this.$store.dispatch("showSnack", {
          message: "Solicitação de saque enviada com sucesso!",
          color: "success",
        });

        this.closeWithdrawModal();
        await this.handleLoadDashboard();
      } catch (error: any) {
        console.error("Erro ao processar saque:", error);
        this.$store.dispatch("showSnack", {
          message: error.response?.data?.message || "Erro ao processar solicitação de saque",
          color: "error",
        });
      } finally {
        this.withdrawLoading = false;
      }
    },
  },
});
</script>

<style scoped lang="scss">
@import "@/assets/scss/design-system/tokens";

.dashboard-page {
  color: $br-ink;
}

.dash-subtitle {
  margin: 0 0 $br-space-4;
  font-size: 1.05rem;
  color: $br-ink-soft;
  font-weight: 700;
  line-height: 1.4;
  letter-spacing: -0.15px;
}

.dash-period-field {
  flex: 1 1 280px;
  min-width: 280px;
  max-width: 100%;
}

@media (max-width: 959px) {
  .dash-period-field {
    flex: 1 1 100%;
    min-width: 100%;
  }
}

.update-chip {
  border-radius: 10px !important;
  font-weight: 600;
  font-size: 12px !important;
  height: 30px !important;
}

.balance-pill {
  border-radius: $br-radius-pill !important;
  border: 1px solid rgba(229, 24, 61, 0.18) !important;
  background: linear-gradient(135deg, rgba(229, 24, 61, 0.08), rgba(255, 255, 255, 0.98)) !important;
  cursor: pointer;
  transition: transform 0.2s ease, box-shadow 0.2s ease;
  min-width: 0;
  box-shadow: $br-shadow-sm !important;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 18px rgba(229, 24, 61, 0.14) !important;
  }
}

.balance-pill__inner {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 8px 14px !important;
}

.balance-pill__text {
  display: flex;
  flex-direction: column;
  gap: 0;
  min-width: 0;
}

.balance-pill__value {
  font-size: 15px;
  font-weight: 700;
  color: $br-ink-soft;
  line-height: 1.2;
}

.balance-pill__label {
  font-size: $br-font-size-xs;
  color: $br-icon-muted;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.3px;
}

.toggle-balances-btn {
  border-radius: 10px !important;
  font-weight: 600 !important;
}

.quick-actions-inline__label {
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.45px;
  color: #9aa3c2;
  margin-right: 4px;
}

.quick-actions-inline__btn {
  min-width: auto !important;
  padding: 0 8px !important;
  height: 32px !important;
  font-size: 13px !important;
  font-weight: 600 !important;
}

.dashboard-page ::v-deep .toolbar-card .v-input .v-input__slot {
  min-height: 40px !important;
}

.kpi-skeleton {
  border-radius: 14px;
  min-height: 108px;
}

.metrics-row {
  margin-top: 4px;
}

.dash-banner {
  border-radius: 10px !important;
  box-shadow: none !important;
}

.dash-banner--onboarding {
  border-color: rgba(200, 160, 0, 0.45) !important;
  background: rgba(255, 248, 225, 0.65) !important;
}

.dash-banner ::v-deep .v-alert__wrapper {
  padding: 6px 10px 6px 8px !important;
  align-items: center;
}

.dash-banner ::v-deep .v-alert__content {
  padding: 0 !important;
  width: 100%;
  margin: 0 !important;
}

.dash-banner__inner {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
}

.dash-banner__lead-icon {
  flex-shrink: 0;
  opacity: 0.9;
}

.dash-banner__copy {
  flex: 1 1 140px;
  min-width: 0;
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 4px 8px;
  line-height: 1.3;
}

.dash-banner__title {
  font-weight: 700;
  font-size: 13px;
  color: #23263a;
}

.dash-banner__hint {
  font-size: 12px;
  color: #5c6478;
  font-weight: 500;
}

.dash-banner__info {
  color: #9aa3b8 !important;
  cursor: help;
  flex-shrink: 0;
  margin-left: -4px;
}

.dash-banner__cta {
  flex-shrink: 0;
  text-transform: none !important;
  font-weight: 600 !important;
  font-size: 12px !important;
  letter-spacing: 0.2px;
  min-height: 30px !important;
  padding: 0 12px !important;
}

@media (max-width: 599px) {
  .dash-banner__inner {
    display: grid;
    grid-template-columns: auto 1fr;
    grid-template-areas:
      "icon copy"
      "cta cta";
    gap: 6px 10px;
    align-items: start;
  }

  .dash-banner__lead-icon {
    grid-area: icon;
    margin-top: 2px;
  }

  .dash-banner__copy {
    grid-area: copy;
  }

  .dash-banner__cta {
    grid-area: cta;
    width: 100%;
  }
}

.dash-banners-row {
  margin-bottom: 10px;
}

.dash-banners-row__col {
  display: flex;
}

@media (min-width: 960px) {
  .dash-banners-row__col {
    padding-left: 6px !important;
    padding-right: 6px !important;
  }
}

@media (max-width: 959px) {
  .balance-pill {
    flex: 1 1 100%;
  }
}
</style>
